import { BLOCKS } from '@contentful/rich-text-types'
import type { Document } from '@contentful/rich-text-types'
import { z } from 'zod'
import type { DemoSession } from '~/types'

const DEMOS_URL = 'https://docs.omni.co/demos/latest.json'
export const DEMOS_PAGE_URL = 'https://docs.omni.co/demos'
const DEMOS_FETCH_TIMEOUT_MS = 4000
const DEMOS_FETCH_RETRIES = 2
const DEMOS_CACHE_TTL_MS = 5 * 60 * 1000

const docsDemoSchema = z.object({
  title: z.string(),
  demoer: z.string(),
  description: z.string(),
  embed_link: z.preprocess(
    (value) => (value === '' ? undefined : value),
    z.string().url().nullable().optional(),
  ),
})

const docsDemoSessionSchema = z.object({
  date: z.string(),
  demos: z.array(docsDemoSchema).min(1),
})

const textDocument = (value: string): Document => ({
  nodeType: BLOCKS.DOCUMENT,
  data: {},
  content: value
    ? [
        {
          nodeType: BLOCKS.PARAGRAPH,
          data: {},
          content: [
            {
              nodeType: 'text',
              value,
              marks: [],
              data: {},
            },
          ],
        },
      ]
    : [],
})

const slugify = (input: string) =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .trim()
    .replace(/\s+/g, '-')

const dateSlug = (date: Date) => {
  const year = date.getUTCFullYear()
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0')
  const day = `${date.getUTCDate()}`.padStart(2, '0')

  return `${year}${month}${day}`
}

export type DocsDemosState = {
  demos: DemoSession
  displayDate: string
}

let cachedDocsDemos: DocsDemosState | undefined
let cachedAt = 0
let inFlight: Promise<DocsDemosState> | undefined

const mapDocsDemos = (
  raw: z.infer<typeof docsDemoSessionSchema>,
): DocsDemosState => {
  const date = new Date(raw.date)

  return {
    displayDate: raw.date,
    demos: {
      date,
      slug: dateSlug(date),
      title: `Engineering demos for ${raw.date}`,
      isLatest: true,
      summary: textDocument('Latest engineering demos from docs.omni.co.'),
      demos: raw.demos.map(({ title, demoer, description, embed_link }) => ({
        title,
        description: textDocument(description),
        slug: slugify(title),
        labels: [],
        presenters: [{ name: demoer, slug: slugify(demoer) }],
        videoUrl: embed_link ?? undefined,
      })),
    },
  }
}

/**
 * Stale beats an empty section, and re-stamping the timestamp holds that copy
 * for a full TTL so an outage cannot put three failing attempts on every render.
 */
const serveStale = (): DocsDemosState | undefined => {
  if (!cachedDocsDemos) return undefined

  cachedAt = Date.now()

  return cachedDocsDemos
}

const fetchFresh = async (): Promise<DocsDemosState> => {
  /* oxlint-disable no-await-in-loop -- retries need to happen sequentially */
  for (let attempt = 0; attempt <= DEMOS_FETCH_RETRIES; attempt += 1) {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(
        () => controller.abort(),
        DEMOS_FETCH_TIMEOUT_MS,
      )

      try {
        const response = await fetch(DEMOS_URL, {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`Docs demos request failed with ${response.status}`)
        }

        const raw = docsDemoSessionSchema.parse(await response.json())
        const docsDemos = mapDocsDemos(raw)
        cachedDocsDemos = docsDemos
        cachedAt = Date.now()

        return docsDemos
      } finally {
        clearTimeout(timeoutId)
      }
    } catch (error) {
      if (attempt === DEMOS_FETCH_RETRIES) {
        const stale = serveStale()
        if (stale) return stale
        throw error
      }
    }
  }
  /* oxlint-enable no-await-in-loop */

  const stale = serveStale()
  if (stale) return stale
  throw new Error('Docs demos request failed')
}

/**
 * The home page loader runs this on every render, so an uncached call puts an
 * outbound round trip on the critical path of the busiest route. Responses are
 * held for the TTL, and concurrent misses share one request so a traffic spike
 * cannot fan out into a request per visitor.
 */
export const fetchDocsDemos = (): Promise<DocsDemosState> => {
  if (cachedDocsDemos && Date.now() - cachedAt < DEMOS_CACHE_TTL_MS) {
    return Promise.resolve(cachedDocsDemos)
  }

  return (inFlight ??= fetchFresh().finally(() => {
    inFlight = undefined
  }))
}
